import React, { useEffect } from 'react';

import {
    ASDRatchets,
    PerformanceDiscounts,
    PlayCountAggregationTypeMappingList,
    StreamingTaxDeductionCaps,
    TerritoryOverrides,
    VendorShareCalculationTypeMappingList,
    VpirBundleRevenueDeduction
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';

import { cloneDeep } from 'lodash';
import { ContractTermInfoButton } from 'src/components/ContractTermInfoButton';
import { LoadingSkeleton } from 'src/components/LoadingSkeleton';
import { PrsDeductionCap } from 'src/components/RateCardDetail/ContractTerms/PrsDeductionCap';
import { validateContractTerm } from 'src/components/RateCardDetail/ContractTerms/contractTermValidation';
import { Toggle } from 'src/components/Toggle';
import { ContractTermName } from 'src/constants/contractTermHelpInfo';
import { defaultContractTermOptionalSection } from 'src/constants/rateCards';
import { IRateCardDetailStepProps } from 'src/types/IRateCard';
import { isEverySectionValid } from 'src/util/validationsUtil';

import { AdditionalSettings } from './AdditionalSettings';
import { AsdRatchets } from './AsdRatchets';
import './ContractTerms.scss';
import { GrowthTarget } from './GrowthTarget';
import { RateMultipliers } from './RateMultipliers';
import { Revenue } from './Revenue';
import { StreamingTaxDeduction } from './StreamingTaxDeduction';
import { SubscriberVolumeDiscount } from './SubscriberVolumeDiscount';
import { ThirdPartyDeductionCaps } from './ThirdPartyDeductionCaps';
import { UmgPerformanceDiscounts } from './UmgPerformanceDiscounts';
import { VendorSharePlayCountCalcStrategy } from './VendorSharePlayCountCalcStrategy';

export const ContractTerms = ({
    rateCardValues,
    setRateCardValues,
    stepTitle,
    isEditMode,
    setIsRateCardInputValid,
    setIsNextDisabled,
    setIsPrevDisabled,
    dataLoaded
}: IRateCardDetailStepProps) => {
    const contractTermsComponents = [
        Revenue,
        VendorSharePlayCountCalcStrategy,
        GrowthTarget,
        UmgPerformanceDiscounts,
        RateMultipliers,
        AsdRatchets,
        ThirdPartyDeductionCaps,
        StreamingTaxDeduction,
        PrsDeductionCap,
        SubscriberVolumeDiscount,
        AdditionalSettings
    ];

    const [optionalSection, setOptionalSection] = React.useState(defaultContractTermOptionalSection);

    const [initContractTermInputValid, initContractTermErrorMessages] = validateContractTerm(
        rateCardValues.contractTerms,
        optionalSection
    );
    const [isContractTermInputValid, setIsContractTermInputValid] = React.useState(initContractTermInputValid);
    const [contractTermErrorMessages, setContractTermErrorMessages] = React.useState(initContractTermErrorMessages);

    useEffect(() => {
        if (!setIsRateCardInputValid) return;
        const [contractTermInputValidity, errMsgs] = validateContractTerm(
            rateCardValues.contractTerms,
            optionalSection
        );
        const isContractTermsValid =
            isEverySectionValid(isContractTermInputValid) && isEverySectionValid(contractTermInputValidity);
        setIsRateCardInputValid((prevState) => ({
            ...prevState,
            contractTerms: isContractTermsValid
        }));
        if (!setIsNextDisabled || !setIsPrevDisabled) return;
        setIsNextDisabled(!isContractTermsValid);
        setIsPrevDisabled(!isContractTermsValid);
    }, [isContractTermInputValid]);

    const handleContractTermValueChange = (
        moduleName: string,
        field: string,
        value:
            | string
            | boolean
            | TerritoryOverrides
            | PerformanceDiscounts
            | ASDRatchets
            | VpirBundleRevenueDeduction
            | VendorShareCalculationTypeMappingList
            | PlayCountAggregationTypeMappingList
            | StreamingTaxDeductionCaps
            | null
            | undefined
    ) => {
        setRateCardValues((prevState) => {
            const clonedRateCardValues = cloneDeep(prevState);
            const clonedContractTerms = clonedRateCardValues.contractTerms;
            const moduleValue =
                field === ''
                    ? value
                    : {
                          // @ts-ignore
                          ...clonedContractTerms[moduleName],
                          [field]: value
                      };
            return {
                ...clonedRateCardValues,
                contractTerms: {
                    ...clonedContractTerms,
                    [moduleName]: moduleValue
                }
            };
        });
    };

    const handleContractTermErrorMessageChange = (moduleName: string, field: string, value: string) => {
        setContractTermErrorMessages((prevState) => {
            return {
                ...prevState,
                [moduleName]: {
                    ...prevState[moduleName],
                    [field]: value
                }
            };
        });
    };

    return (
        <Column spacing="500" height="100%">
            {dataLoaded ? (
                <>
                    <Row alignmentHorizontal={'justify'} className="contract-terms-width">
                        <Heading level={2}>{stepTitle || 'Contract Terms'}</Heading>
                        <Row>
                            <Column spacing={'200'}>
                                <Text type="h100">Blended PSM</Text>
                                <Toggle
                                    label={
                                        rateCardValues.coreRates.length !== 0 &&
                                        rateCardValues.coreRates.some(
                                            (coreRate) =>
                                                coreRate['Blended PSM Floor'] !== undefined &&
                                                coreRate['Blended PSM Floor'] !== ''
                                        )
                                            ? 'On'
                                            : 'Off'
                                    }
                                    value={
                                        rateCardValues.coreRates.length !== 0 &&
                                        rateCardValues.coreRates.some(
                                            (coreRate) =>
                                                coreRate['Blended PSM Floor'] !== undefined &&
                                                coreRate['Blended PSM Floor'] !== ''
                                        )
                                    }
                                    isEditMode={false}
                                />
                            </Column>
                            <Column spacing={'200'}>
                                <Row spacing="none">
                                    <Text type="h100">90 Day FT CPP</Text>
                                    <ContractTermInfoButton
                                        contractTermName={ContractTermName.BlendedPSMAnd90DayFTCPP}
                                    />
                                </Row>

                                <Toggle
                                    label={
                                        rateCardValues.coreRates.length !== 0 &&
                                        '90 Day FT CPP' in rateCardValues.coreRates[0]
                                            ? 'On'
                                            : 'Off'
                                    }
                                    value={
                                        rateCardValues.coreRates.length !== 0 &&
                                        '90 Day FT CPP' in rateCardValues.coreRates[0]
                                    }
                                    isEditMode={false}
                                />
                            </Column>
                        </Row>
                    </Row>
                    <Column spacing="500" className="contract-terms-width">
                        {contractTermsComponents.map((ContractTermsComponent, index) => (
                            <ContractTermsComponent
                                key={index}
                                contractTerms={rateCardValues.contractTerms}
                                handleContractTermValueChange={handleContractTermValueChange}
                                isEditMode={isEditMode}
                                isContractTermInputValid={isContractTermInputValid}
                                setIsContractTermInputValid={setIsContractTermInputValid}
                                coreRate={rateCardValues.coreRates}
                                contractTermErrorMessages={contractTermErrorMessages}
                                handleContractTermErrorMessageChange={handleContractTermErrorMessageChange}
                                optionalSection={optionalSection}
                                setOptionalSection={setOptionalSection}
                            />
                        ))}
                    </Column>
                </>
            ) : (
                <LoadingSkeleton />
            )}
        </Column>
    );
};
